import { useState } from "react";
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { useAnalyticsQuery } from "@/hooks/useAnalyticsQuery";
import { analyticsApi } from "@/services/apiClient";
import { todayISODate } from "@/stores/filterStore";
import KpiCard, { KpiRow } from "@/components/KpiCard";
import ToggleGroup from "@/components/ToggleGroup";
import MiniSelect from "@/components/MiniSelect";
import SectionRangePicker from "@/components/SectionRangePicker";
import ChartFrame from "@/components/ChartFrame";
import InfoTooltip from "@/components/InfoTooltip";
import { endpointDot } from "@/lib/chartStyle";
import { fmt } from "@/lib/format";
import Panel from "./Panel";
import Pagination from "@/components/Pagination";
import Skeleton from "./Skeleton";

// 레거시 사내툴(AI-NOVA-V1) 사용 현황 (2026-07-20) — 옛 dashboard.sedaily.ai 를 novadb 로 흡수.
// 사내 기자들이 매일 쓰는 인하우스 7개 툴(제목·교열·보도·외신·퇴고·버디·멀티AI). 신규 노바
// (무료 개통)와 별개 세계 — 청구가 없어 "누가·무엇을·얼마나 쓰나"가 관리 질문(원가 없음).
// 일/주/월 시계열은 옛 대시보드 방식(usage 행에서 날짜 추출) 그대로. 월별은 정확, 일·주는
// 월별 집계 테이블(제목·보도·M1)의 경우 행 생성일에 몰려 찍히는 근사치(옛 대시보드 동일).

const GRAN_OPTIONS = [
  { id: "day", label: "일별" },
  { id: "week", label: "주별" },
  { id: "month", label: "월별" },
];
const MODE_OPTIONS = [
  { id: "interval", label: "구간" },
  { id: "cumulative", label: "누적" },
];
const SERVICE_LABELS = {
  title: "제목", proofread: "교열", news: "보도", foreign: "외신",
  revision: "퇴고", buddy: "버디", m1: "멀티 AI",
};

// 레거시 데이터는 2025-08 부터 — 기본 범위를 그 이전(2025-01-01)부터로 넓혀 '전체'가
// 정말 전 구간을 담게 한다(차트는 데이터 있는 첫 달부터 그려짐).
function defaultRange() {
  return { from: "2025-01-01", to: todayISODate() };
}

export default function LegacyInternalView() {
  const [gran, setGran] = useState("month");
  const [mode, setMode] = useState("interval"); // 구간 | 누적
  const [range, setRange] = useState(defaultRange);
  const [service, setService] = useState("");
  const [engine, setEngine] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "messages", dir: "desc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const q = useAnalyticsQuery(
    (p, s) => analyticsApi.legacyInternal(
      { granularity: gran, from: range.from, to: range.to, service, engine }, s),
    [gran, range.from, range.to, service, engine]
  );

  if (q.loading && !q.data) return <Skeleton />;
  if (q.error)
    return <Panel title="레거시 사내툴"><p className="text-sm text-red-600">불러오기 실패 — {String(q.error.message || q.error)}</p></Panel>;

  const d = q.data || {};
  const t = d.totals || {};
  const services = d.services || [];
  const serviceEngines = d.serviceEngines || {};
  const byModel = d.byModel || [];
  const byEngine = d.byEngine || [];
  let cumMsg = 0, cumTok = 0;
  const series = (d.timeseries || []).map((r) => {
    cumMsg += r.messages; cumTok += r.tokens;
    return {
      ...r,
      label: gran === "month" ? r.bucket.slice(2) : r.bucket.slice(5),
      dispMsg: mode === "cumulative" ? cumMsg : r.messages,
      dispTok: mode === "cumulative" ? cumTok : r.tokens,
      dispUsers: mode === "cumulative" ? r.cumUsers : r.activeUsers,
      cumRegistered: r.cumRegistered,
    };
  });
  const maxMsg = Math.max(1, ...services.map((s) => s.messages));
  const maxModel = Math.max(1, ...byModel.map((m) => m.messages));
  const maxEngine = Math.max(1, ...byEngine.map((e) => e.messages));

  // 서비스/엔진 필터 옵션 (엔진은 선택 서비스에 종속, 라벨은 백엔드 순번 매핑)
  const serviceOpts = [{ value: "", label: "전체 서비스" },
    ...Object.keys(SERVICE_LABELS).map((id) => ({ value: id, label: SERVICE_LABELS[id] }))];
  const engineOpts = [{ value: "", label: "전체 엔진" },
    ...(service ? (serviceEngines[service] || []).map((e) => ({ value: e.value, label: e.label })) : [])];

  // 기자 표 — 클라이언트 정렬 + 검색
  const setSortKey = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  const users = (d.users || [])
    .filter((u) => !search || (u.email || u.userId).toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const mul = sort.dir === "desc" ? -1 : 1;
      const va = a[sort.key], vb = b[sort.key];
      if (typeof va === "string") return mul * (va || "").localeCompare(vb || "");
      return mul * ((va || 0) - (vb || 0));
    });
  const safePage = Math.min(page, Math.max(1, Math.ceil(users.length / pageSize)));
  const pageUsers = users.slice((safePage - 1) * pageSize, (safePage - 1) * pageSize + pageSize);

  return (
    <div className="space-y-4">
      {/* 필터 툴바 — 라벨 단위로 그룹핑해 한눈에 정돈. 좌: 보기(세분화·누적) / 중: 대상
          (서비스·엔진) / 우: 기간. 좁은 화면에선 줄바꿈. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-xl border border-ink-100 bg-white px-3.5 py-2.5">
        <FilterGroup label="보기">
          <ToggleGroup options={GRAN_OPTIONS} value={gran} onChange={setGran} />
          <ToggleGroup options={MODE_OPTIONS} value={mode} onChange={setMode} />
        </FilterGroup>
        <FilterGroup label="대상">
          <MiniSelect value={service} options={serviceOpts}
                      onChange={(v) => { setService(v); setEngine(""); setPage(1); }} />
          <MiniSelect value={engine} options={engineOpts}
                      onChange={(v) => { setEngine(v); setPage(1); }} />
          {(service || engine) && (
            <button onClick={() => { setService(""); setEngine(""); setPage(1); }}
                    className="text-[12px] text-ink-400 underline-offset-2 hover:text-ink-600 hover:underline">초기화</button>
          )}
        </FilterGroup>
        <FilterGroup label="기간" className="ml-auto">
          <SectionRangePicker from={range.from} to={range.to} fullFrom="2025-01-01"
                              onChange={(from, to) => setRange({ from, to })} />
        </FilterGroup>
      </div>

      {/* 필터 전환 중에도 직전 데이터를 유지하고 살짝만 흐리게 — 클릭 즉시 컨트롤은 반응하고
          콘텐츠는 조용히 갱신된다(스켈레톤 깜빡임 없음). */}
      <div className={`space-y-4 transition-opacity duration-200 ${q.loading ? "opacity-50" : "opacity-100"}`}>

      <KpiRow cols={4}>
        <KpiCard label="가입 사용자" value={`${fmt.int(t.rosterCount ?? 0)}명`} tone="brand" hero
                 sublabel="사내 CMS 풀 등록 계정 전체" />
        <KpiCard label="사용 기자" value={`${fmt.int(t.activeUsers ?? 0)}명`} tone="good"
                 sublabel={t.rosterCount ? `가입 대비 ${Math.round((t.activeUsers / t.rosterCount) * 100)}% — 실제 인하우스 툴 사용` : "인하우스 툴을 쓴 기자"} />
        <KpiCard label={`최근 ${d.recentDays ?? 30}일 활성`} value={`${fmt.int(t.recentUsers ?? 0)}명`}
                 sublabel="지금도 실제로 쓰는 기자 규모" />
        <KpiCard label="누적 요청" value={fmt.int(t.messages ?? 0)}
                 sublabel={`토큰 ${fmt.compact(t.tokens ?? 0)} · 선택 조건`} />
      </KpiRow>

      <Panel title={mode === "cumulative" ? "누적 사용 추이" : "사용 추이"}
             action={<InfoTooltip text="usage 테이블에서 날짜를 뽑아 버킷팅(옛 대시보드 방식). 면적=요청 수(누적 모드는 그때까지 합계), 선=구간 활성 기자 / 누적 모드는 누적 가입자(그 시점까지 만들어진 계정). 월별은 정확, 일·주별은 월 단위로만 집계되는 툴(제목·보도·멀티AI)의 경우 근사치입니다." />}>
        {series.length ? (
          <ChartFrame base="h-72 sm:h-[24rem]" tall="h-[28rem] sm:h-[36rem]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="legMsg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563eb" stopOpacity={0.20} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0.01} />
                  </linearGradient>
                  <linearGradient id="legReg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.16} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.01} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="#f2f2f0" strokeDasharray="0" />
                <XAxis dataKey="label" tick={{ fill: "#9a9a96", fontSize: 11 }} axisLine={false} tickLine={false} tickMargin={10} minTickGap={18} />
                <YAxis yAxisId="l" tick={{ fill: "#9a9a96", fontSize: 11 }} width={44} axisLine={false} tickLine={false}
                       tickFormatter={(v) => fmt.compact(v)} />
                <YAxis yAxisId="r" orientation="right" tick={{ fill: "#9a9a96", fontSize: 11 }} width={34} axisLine={false} tickLine={false}
                       domain={mode === "cumulative" && t.rosterCount ? [0, Math.ceil(t.rosterCount * 1.08)] : [0, "auto"]} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 14, border: "none", boxShadow: "0 6px 20px rgba(15,23,42,0.14)", fontSize: 12, padding: "8px 12px" }}
                  formatter={(val, name) => /기자|가입/.test(name) ? [`${val}명`, name] : [fmt.int(val), name]} />
                <Legend verticalAlign="top" height={30} iconType="circle" iconSize={8}
                        wrapperStyle={{ fontSize: 12, color: "#6b6b68", paddingBottom: 6 }} />
                <Area yAxisId="l" type="natural" dataKey="dispMsg" name={mode === "cumulative" ? "누적 요청" : "요청 수"}
                      stroke="#2563eb" strokeWidth={2.25} fill="url(#legMsg)" dot={false} activeDot={{ r: 3.5 }} />
                {mode === "cumulative" ? (
                  /* 누적 가입자 — 그 시점까지 만들어진 계정 수(→ 로스터 총원 도달). 부드러운 곡선. */
                  <Area yAxisId="r" type="natural" dataKey="cumRegistered" name="누적 가입자"
                        stroke="#f59e0b" strokeWidth={2.75} fill="url(#legReg)"
                        dot={endpointDot("#f59e0b", series.length)} activeDot={{ r: 4 }} />
                ) : (
                  <Line yAxisId="r" type="natural" dataKey="dispUsers" name="활성 기자"
                        stroke="#10b981" strokeWidth={2.75}
                        dot={endpointDot("#10b981", series.length)} activeDot={{ r: 4 }} />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </ChartFrame>
        ) : (
          <p className="py-8 text-center text-sm text-ink-400">이 조건에 사용 기록이 없습니다.</p>
        )}
      </Panel>

      {/* 모델별 / 엔진별 분해 — ARN 을 실모델로 해석해 쪼갠다. 엔진 분해는 서비스 선택 시.
          제목·조건은 응답 데이터 기준(d.service)이라 로딩 중 제목만 먼저 바뀌는 stale 없음. */}
      <div className={`grid gap-4 ${d.service ? "lg:grid-cols-2" : ""}`}>
        <Panel title="모델별 사용"
               action={<InfoTooltip text="lastModelId 기준. application-inference-profile ARN 은 Bedrock 에서 실모델로 해석. '미상'은 모델을 기록하지 않던 옛 행입니다." />}>
          {byModel.length ? (
            <div className="space-y-1.5">
              {byModel.map((m) => (
                <div key={m.model} className="flex items-center gap-3 px-1.5 py-1">
                  <span className="w-20 shrink-0 text-[13px] text-ink-600">{m.model}</span>
                  <span className="relative h-6 flex-1 overflow-hidden rounded-md bg-ink-100/70">
                    <span className="absolute inset-y-0 left-0 block rounded-md bg-slate-300"
                          style={{ width: `${Math.max(1.5, (m.messages / maxModel) * 100)}%` }} />
                    <span className="absolute inset-y-0 left-2.5 flex items-center text-[12px] font-medium tabular-nums text-ink-600">{fmt.int(m.messages)}</span>
                  </span>
                  <span className="w-14 shrink-0 text-right text-[12px] tabular-nums text-ink-400">{m.activeUsers}명</span>
                </div>
              ))}
            </div>
          ) : <p className="py-6 text-center text-sm text-ink-400">데이터 없음</p>}
        </Panel>

        {d.service && (
          <Panel title={`${SERVICE_LABELS[d.service] || d.service} 엔진별 사용`}
                 action={<InfoTooltip text={d.service === "m1" ? "멀티 AI 는 고른 모델(Claude·GPT·Perplexity)별 사용량입니다." : "선택 서비스의 엔진(작업 유형)별 사용량. 코드가 다른 서비스와 겹쳐도 뜻은 서비스마다 다릅니다."} />}>
            {byEngine.length ? (
              <div className="space-y-1.5">
                {byEngine.map((e) => (
                  <button key={e.engine} onClick={() => { setEngine(e.engine); setPage(1); }}
                          className="flex w-full items-center gap-3 rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-ink-50">
                    <span className="w-24 shrink-0 text-[13px] text-ink-600">
                      {e.label}{e.engine.length <= 4 && e.label !== e.engine && <span className="ml-1 text-[11px] text-ink-400">({e.engine})</span>}
                    </span>
                    <span className="relative h-6 flex-1 overflow-hidden rounded-md bg-ink-100/70">
                      <span className="absolute inset-y-0 left-0 block rounded-md bg-slate-300"
                            style={{ width: `${Math.max(1.5, (e.messages / maxEngine) * 100)}%` }} />
                      <span className="absolute inset-y-0 left-2.5 flex items-center text-[12px] font-medium tabular-nums text-ink-600">{fmt.int(e.messages)}</span>
                    </span>
                    <span className="w-14 shrink-0 text-right text-[12px] tabular-nums text-ink-400">{e.activeUsers}명</span>
                  </button>
                ))}
              </div>
            ) : <p className="py-6 text-center text-sm text-ink-400">데이터 없음</p>}
          </Panel>
        )}
      </div>

      <Panel title="툴별 사용량"
             action={<InfoTooltip text="선택한 날짜·엔진 범위의 툴별 요청 수. 서비스 필터와 무관하게 항상 7개 툴을 비교합니다. 툴 이름을 누르면 그 툴로 좁혀집니다." />}>
        <div className="space-y-1.5">
          {services.map((s) => {
            const on = s.id === service;
            const dim = service && !on;
            return (
              <button key={s.id} onClick={() => { setService(on ? "" : s.id); setEngine(""); setPage(1); }}
                      className="flex w-full items-center gap-3 rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-ink-50">
                <span className={`w-16 shrink-0 text-[13px] ${on ? "font-semibold text-ink-900" : "text-ink-600"}`}>
                  {SERVICE_LABELS[s.id] || s.label}
                </span>
                <span className="relative h-6 flex-1 overflow-hidden rounded-md bg-ink-100/70">
                  <span className="absolute inset-y-0 left-0 rounded-md transition-all"
                        style={{ width: `${Math.max(1.5, (s.messages / maxMsg) * 100)}%` }}
                        data-on={on}>
                    <span className={`block h-full w-full rounded-md ${on ? "bg-brand-500" : dim ? "bg-slate-200" : "bg-slate-300"}`} />
                  </span>
                  <span className="absolute inset-y-0 left-2.5 flex items-center text-[12px] font-medium tabular-nums text-ink-600">
                    {fmt.int(s.messages)}
                  </span>
                </span>
                <span className="w-14 shrink-0 text-right text-[12px] tabular-nums text-ink-400">{s.activeUsers}명</span>
              </button>
            );
          })}
        </div>
      </Panel>

      <Panel
        title="사내 기자 사용 순위"
        action={
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                 placeholder="이메일 검색"
                 className="w-40 rounded-lg border border-ink-200 px-2.5 py-1.5 text-[12.5px] focus:border-brand-400 focus:outline-none" />
        }>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-[13px]">
            <thead>
              <tr className="text-left text-ink-400">
                <th className="pb-2 font-medium">#</th>
                <SortTh k="email" sort={sort} onSort={setSortKey}>기자</SortTh>
                <SortTh k="serviceCount" sort={sort} onSort={setSortKey}>사용 도구</SortTh>
                <SortTh k="messages" sort={sort} onSort={setSortKey} align="right">요청</SortTh>
                <SortTh k="tokens" sort={sort} onSort={setSortKey} align="right">토큰</SortTh>
                <SortTh k="lastUsed" sort={sort} onSort={setSortKey} align="right">최근 사용</SortTh>
              </tr>
            </thead>
            <tbody>
              {pageUsers.map((u, i) => (
                <tr key={u.userId} className="border-t border-ink-100">
                  <td className="py-2 tabular-nums text-ink-400">{(safePage - 1) * pageSize + i + 1}</td>
                  <td className="py-2">
                    {u.email || <span className="font-mono text-[12px] text-ink-400">{u.userId.slice(0, 18)}…</span>}
                  </td>
                  <td className="py-2">
                    <div className="flex flex-wrap gap-1">
                      {(u.services || []).map((sid) => (
                        <span key={sid} className="rounded bg-ink-100 px-1.5 py-0.5 text-[11px] text-ink-600">
                          {SERVICE_LABELS[sid] || sid}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 text-right tabular-nums">{fmt.int(u.messages)}</td>
                  <td className="py-2 text-right tabular-nums text-ink-500">{fmt.compact(u.tokens)}</td>
                  <td className="py-2 text-right tabular-nums text-ink-400">{u.lastUsed?.slice(0, 10) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination total={users.length} page={safePage} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={setPageSize} />
          {!users.length && <p className="py-6 text-center text-sm text-ink-400">조건에 맞는 기자가 없습니다.</p>}
        </div>
        <p className="mt-2 text-[12px] text-ink-400">
          레거시 AI-NOVA-V1 인하우스 툴 사용량(옛 dashboard.sedaily.ai 통합). 무과금이라 원가 없이
          요청·토큰만 집계합니다.
        </p>
      </Panel>
      </div>
    </div>
  );
}

function FilterGroup({ label, className = "", children }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className="text-[11px] font-medium uppercase tracking-wide text-ink-400">{label}</span>
      {children}
    </div>
  );
}

function SortTh({ k, sort, onSort, align = "left", children }) {
  const active = sort.key === k;
  const Icon = !active ? ChevronsUpDown : sort.dir === "desc" ? ChevronDown : ChevronUp;
  return (
    <th className={`pb-2 font-medium ${align === "right" ? "text-right" : "text-left"}`}>
      <button onClick={() => onSort(k)}
              className={`inline-flex items-center gap-0.5 ${align === "right" ? "flex-row-reverse" : ""} ${active ? "text-ink-700" : "text-ink-400 hover:text-ink-600"}`}>
        {children}
        <Icon className="h-3.5 w-3.5" />
      </button>
    </th>
  );
}
